/* eslint-disable camelcase */

import { createAction } from '@reduxjs/toolkit';
import { API, Dashboard, ErrorObject, UUID } from 'types';
import { Condition, DataNode, Tree, ValueNode } from 'utils/parserTree';

export const fetchAllDashboards = createAction('DASHBOARDS/FETCH');
export const fetchAllDashboardsRequest = createAction('DASHBOARDS/FETCH_REQUEST');
export const fetchAllDashboardsFailure = createAction<ErrorObject>('DASHBOARDS/FETCH_FAILURE');

export type FetchAllDashboardsSuccessPayload = API.WrappedAPIResponse<Dashboard>;
export const fetchAllDashboardsSuccess = createAction<FetchAllDashboardsSuccessPayload>(
  'DASHBOARDS/FETCH_SUCCESS'
);

export const createDashboard = createAction('DASHBOARDS/CREATE');
export const createDashboardRequest = createAction('DASHBOARDS/CREATE_REQUEST');
export const createDashboardSuccess = createAction<Dashboard>('DASHBOARDS/CREATE_SUCCESS');
export const createDashboardFailure = createAction<ErrorObject>('DASHBOARDS/CREATE_FAILURE');

export const deleteDashboard = createAction<{ id: UUID }>('DASHBOARDS/DELETE');
export const deleteDashboardRequest = createAction<string>('DASHBOARDS/DELETE_REQUEST');
export const deleteDashboardSuccess = createAction<string>('DASHBOARDS/DELETE_SUCCESS');
export const deleteDashboardFailure = createAction<ErrorObject>('DASHBOARDS/DELETE_FAILURE');

export const upsertDashboard = createAction<Dashboard>('DASHBOARDS/UPSERT');
export const upsertDashboardRequest = createAction<Dashboard>('DASHBOARDS/UPSERT_REQUEST');
export const upsertDashboardSuccess = createAction<Dashboard>('DASHBOARDS/UPSERT_SUCCESS');
export const upsertDashboardFailure = createAction<ErrorObject>('DASHBOARDS/UPSERT_FAILURE');

export const setActiveDashboard = createAction<UUID | undefined>('DASHBOARDS/SET_ACTIVE');

export type TimeRange = {
  after: string;
  before: string;
};

export const setTimeRange = createAction<TimeRange>('DASHBOARDS/SET_TIME_RANGE');

export const signalExporting = createAction('DASHBOARDS/SIGNAL_EXPORTING');

export const checkSelectedWidgets = createAction<string>('DASHBOARDS/CHECK_SELECTED_WIDGETS');
export const clearSelectedWidgets = createAction('DASHBOARDS/CLEAR_SELECTED_WIDGETS');

// ------------------------------------------------------------------------------
// ------------------------------------------------------------------------------
// ------------------------------------------------------------------------------
// ------------------------------------------------------------------------------
// ------------------------------------------------------------------------------
// ------------------------------------------------------------------------------

export type SelectedFilterPayload = { node: ValueNode };

export const addSelectedFilter = createAction<SelectedFilterPayload>(
  'DAHSBOARDS/ADD_SELECTED_FILTER'
);
export const removeSelectedFilter = createAction<SelectedFilterPayload>(
  'DAHSBOARDS/REMOVE_SELECTED_FILTER'
);
export const clearSelectedFilters = createAction('DAHSBOARDS/CLEAR_SELECTED_FILTERS');

export type SelectedFieldPayload = { node: DataNode };

export const addSelectedField = createAction<SelectedFieldPayload>('DAHSBOARDS/ADD_SELECTED_FIELD');
export const removeSelectedField = createAction<SelectedFieldPayload>(
  'DAHSBOARDS/REMOVE_SELECTED_FIELD'
);
export const clearSelectedFields = createAction('DAHSBOARDS/CLEAR_SELECTED_FIELD');

export type AddValueToTreePayload = {
  field: string;
  value: string;
  label?: string;
  isSingleValue?: boolean;
};

export const addValueToTree = createAction<AddValueToTreePayload>('DAHSBOARDS/ADD_VALUE_TO_TREE');

export type AddValueToGroupPayload = { nodes: ValueNode[]; group: DataNode };
export const addValuesToExistingGroup = createAction<AddValueToGroupPayload>(
  'DAHSBOARDS/ADD_VALUE_TO_EXISTING_GROUP'
);
export const addValuesToNewGroup = createAction<AddValueToGroupPayload>(
  'DAHSBOARDS/ADD_VALUE_TO_NEW_GROUP'
);

export type RemoveValuePayload = {
  value: string;
  field?: string;
  fieldId?: string;
};
export const removeValueFromTree = createAction<RemoveValuePayload>(
  'DAHSBOARDS/REMOVE_VALUE_FROM_TREE'
);

export type RemoveValuesPayload = {
  values: string[];
  field?: string;
};
export const removeValuesFromTree = createAction<RemoveValuesPayload>(
  'DAHSBOARDS/REMOVE_VALUES_FROM_TREE'
);

export const removeFieldFromTree =
  createAction<{ field: string; id?: string }>('DAHSBOARDS/REMOVE_FIELD');

export type AddAndRemoveValuesToTreePayload = {
  valuesToAdd: AddValueToTreePayload[];
  fieldsToRemove: string[];
};

export const addAndRemoveValuesToTree = createAction<AddAndRemoveValuesToTreePayload>(
  'DAHSBOARDS/ADD_AND_REMOVE_VALUE_TO_TREE'
);

export type ChangeConditionalTypePayload = {
  node: DataNode;
  condition: Condition;
};
export const changeConditionalType = createAction<ChangeConditionalTypePayload>(
  'DAHSBOARDS/CHANGE_CONDITIONAL_TYPE'
);

export const changeConditionalByFieldValue = createAction<{
  field: string;
  condition: Condition;
}>('DAHSBOARDS/CHANGE_CONDITIONAL_BY_FIELD_VALUE');

export const setTree = createAction<{ tree: Tree }>('DAHSBOARDS/SET_TREE');

export const addValuesToGroup = createAction<{ nodes: ValueNode[] }>(
  'DAHSBOARDS/ADD_VALUE_TO_GROUPS'
);

export const removeValuesFromGroup = createAction<{ nodes: ValueNode[] }>(
  'DAHSBOARDS/REMOVE_VALUE_FROM_GROUPS'
);

export const addFieldsToGroup = createAction<{ nodes: DataNode[] }>(
  'DAHSBOARDS/ADD_FIELD_TO_GROUPS'
);

export const removeFieldsFromGroup = createAction<{ nodes: DataNode[] }>(
  'DAHSBOARDS/REMOVE_FIELD_FROM_GROUPS'
);

export const requestTreeFiltersToogle = createAction<{ value: boolean }>(
  'DAHSBOARDS/REQUEST_TREE_FILTERS'
);

export const addAllValuesToField = createAction<{
  nodes: { value: string; label?: string }[];
  field: string;
}>('DAHSBOARDS/ADD_ALL_VALUES_TO_FIELD');

export const clearTree = createAction('DAHSBOARDS/CLEAR_TREE');
